<script lang="ts">
	import { create, fromJson, type JsonObject } from "@bufbuild/protobuf";
	import { timestampDate } from "@bufbuild/protobuf/wkt";
	import { Heading, Indicator } from "flowbite-svelte-v1";
	import pluralize from "pluralize";
	import { getContext, onMount } from "svelte";
	import { fade } from "svelte/transition";
	import { twJoin } from "tailwind-merge";
	import type { BrandKit } from "#gen/brands/v1/service_pb.js";
	import { ConversionType } from "#gen/bundles/v1/bundle_pb.js";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import { EndSessionResponseSchema, PollAnswerStatsSchema, PollSchema, QuestionSchema, QuestionStatsSchema, type QuestionStats } from "#gen/forms/v1/poll_pb.js";
	import type { PollsClient } from "#lib/api/polls.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import type { ComponentProps } from "#lib/components/page/ComponentProps.js";
	import PollCountdown from "#lib/components/page/components/poll/PollCountdown.svelte";
	import PollResults from "#lib/components/page/components/poll/PollResults.svelte";
	import Question from "#lib/components/page/components/poll/Question.svelte";
	import { DEFAULT_ANSWER_LABEL, DEFAULT_COMPLETE_BUTTON_LABEL, DEFAULT_HELPER_LABEL, DEFAULT_NEXT_BUTTON_LABEL, DEFAULT_RESTART_BUTTON_LABEL, DEFAULT_RESULTS_HELPER_LABEL, DEFAULT_SKIP_BUTTON_LABEL } from "#lib/components/page/components/poll/constants.js";
	import { PollState, getNextState, getPollState } from "#lib/components/page/components/poll/stateMachine.js";
	import { getCompleteButtonSize, getPollStyleVars, getRestartButtonSize, getSkipButtonSize, getSubmitButtonSize } from "#lib/components/page/components/poll/theme.js";
	import { transformURL } from "#lib/image.js";
	import { Logger } from "#lib/logging.js";
	import { formatNumber } from "#lib/number.js";
	import { listValue, objectValue, stringValue, structValue } from "#lib/struct.js";
	import Button from "#lib/ui/Button/index.js";
	import type { Writable } from "svelte/store";

	const editorState: Writable<Record<string, unknown>> = getContext("editorState");
	const pageState: Writable<Record<string, unknown>> = getContext("pageState");
	const brandKit: Writable<BrandKit | undefined> = getContext("brandKit");
	const pollsClient: PollsClient = getContext("pollsClient");
	const cookie: string = getContext("cookie");
	const execute: (conversionType: ConversionType, conversionComponent: string, sourceAnchor?: string) => Promise<boolean> = getContext("execute");
	const reload: () => Promise<void> = getContext("reload");

	let { id, page, data, mode, readOnly }: ComponentProps = $props();

	const response = $derived(structValue($data, id));
	const sessionId = $derived(stringValue(response, "sessionId") ?? "");
	const poll = $derived(fromJson(PollSchema, objectValue(response, "poll") ?? {}, { ignoreUnknownFields: true }));
	const answers = $derived((listValue(structValue($data, id), "questionStats") ?? []).map(o => fromJson(QuestionStatsSchema, o, { ignoreUnknownFields: true })));

	// svelte-ignore state_referenced_locally
	let results: QuestionStats[] = $state(answers);
	// svelte-ignore state_referenced_locally
	let stats: QuestionStats[] = $state(getPollState(poll) === PollState.Closed ? answers : []);
	let overridePollState: PollState | undefined = $state();
	let overrideSelection: boolean | undefined = $state();
	let pollState: PollState = $derived(overridePollState ?? getPollState(poll));

	let remainingTime = $state(calculateTimeLeft());
	let complete = $state(false);
	let currentQuestionIndex = $state(getStartIndex());
	let currentQuestion = $derived(poll.questions[currentQuestionIndex] ?? create(QuestionSchema, {}));
	let hasResult: boolean = $derived(overrideSelection ?? !!results[currentQuestionIndex]);
	let loading = $state(false);
	let lastError: unknown = $state();

	let intervalId: ReturnType<typeof setInterval> | undefined;

	const config = $derived(structValue($page.config, id) ?? {});

	onMount(() => {
		if (mode !== Mode.EDITING) {
			scheduleCountdown();
		}
		return () => {
			terminateCountdown();
		};
	});

	$effect(() => {
		if ($editorState && $editorState[poll.id]) {
			const state = $editorState[poll.id] as JsonObject;
			currentQuestionIndex = (state.currentQuestionIndex as number) ?? currentQuestionIndex;
			overridePollState = state.overridePollState as PollState | undefined;
			overrideSelection = state.overrideSelection as boolean | undefined;
		}
	});

	$effect(() => {
		if (stats[currentQuestionIndex]) {
			$pageState[id] = { state: "results" };
		} else {
			$pageState[id] = { state: "voting" };
		}
	});

	export function value(out: Record<string, unknown>) {
		out[id] = response;
	}

	function scheduleCountdown() {
		if (intervalId) {
			clearInterval(intervalId);
		}
		intervalId = setInterval(() => {
			remainingTime = calculateTimeLeft();
			if (overridePollState !== undefined) {
				return;
			}
			const nextState = getNextState(poll, pollState);
			if (nextState !== undefined) {
				if (nextState === PollState.Closed) {
					closePoll();
				}
				pollState = nextState;
			}
		}, 1000);
	}

	function terminateCountdown() {
		if (intervalId) {
			clearInterval(intervalId);
		}
		remainingTime = { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
	}

	function getStartIndex(): number {
		if (getPollState(poll) === PollState.Closed) {
			return poll.questions.length - 1;
		} else {
			const index = results.findIndex(r => !r.userPollAnswer);
			if (index !== -1) {
				return index;
			} else {
				return Math.min(results.length, Math.max(poll.questions.length - 1, 0));
			}
		}
	}

	function calculateTimeLeft() {
		const difference = +(poll.window?.end ? timestampDate(poll.window.end) : new Date()) - +new Date();
		let timeLeft = { days: 0, hours: 0, minutes: 0, seconds: 0, expired: false };
		if (difference > 0) {
			timeLeft = {
				days: Math.floor(difference / (1000 * 60 * 60 * 24)),
				hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
				minutes: Math.floor((difference / 1000 / 60) % 60),
				seconds: Math.floor((difference / 1000) % 60),
				expired: false,
			};
		} else {
			timeLeft.expired = true;
		}
		return timeLeft;
	}

	function closePoll() {
		pollState = PollState.Closed;
		currentQuestionIndex = poll.questions.length - 1;
	}

	async function pickWinner(value: string) {
		if (loading) {
			return;
		}
		loading = true;
		try {
			if (mode === Mode.LIVE) {
				results[currentQuestionIndex] = await pollsClient.submitAnswer(poll.id, currentQuestion.id, sessionId, cookie, value);
			} else {
				results[currentQuestionIndex] = await simulateVote(value);
			}
			lastError = undefined;
		} catch (err) {
			lastError = err;
			Logger.error("Error submitting poll answer", { err });
		} finally {
			loading = false;
		}
	}

	async function simulateVote(value: string) {
		const result = await pollsClient.getQuestionStats(poll.id, currentQuestion.id, sessionId, cookie);
		result.totalPollAnswers++;
		result.pollAnswerStats.forEach(stat => {
			if (stat.pollAnswer?.value === value) {
				stat.count++;
			}
			stat.percentage = (Number(stat.count) / Number(result.totalPollAnswers)) * 100;
		});
		result.userPollAnswer = poll.questions[currentQuestionIndex].pollAnswers.find(a => a.value === value);
		return result;
	}

	function skipQuestion() {
		nextQuestion();
	}

	function nextQuestion() {
		if (currentQuestionIndex < poll.questions.length - 1) {
			currentQuestionIndex += 1;
		} else {
			completePoll();
		}
	}

	async function completePoll() {
		if (loading) {
			return;
		}
		loading = true;
		try {
			if (mode === Mode.LIVE) {
				const res = await execute(ConversionType.POLL_COMPLETED, id, id);
				stats = questionStatsFromExecuteResult(res, id) ?? [...results];
			} else {
				const res = await pollsClient.getPollStats(poll.id, sessionId, cookie);
				stats = [...res].map((result, i) => {
					if (results[i]) {
						return results[i];
					}
					return result;
				});
			}
			complete = true;
			lastError = undefined;
		} catch (err) {
			lastError = err;
			Logger.error("Error fetching poll stats", { err });
		} finally {
			loading = false;
		}
	}

	function questionStatsFromExecuteResult(res: JsonObject | boolean, componentId: string): QuestionStats[] | undefined {
		if (!res || typeof res !== "object") {
			return undefined;
		}
		// PostData EndSession payload is keyed by component id on the execute result.
		const payload = structValue(res, componentId);
		if (!payload) {
			return undefined;
		}
		const endSession = fromJson(EndSessionResponseSchema, payload, { ignoreUnknownFields: true });
		return endSession?.questionStats.length ? endSession.questionStats : undefined;
	}

	async function restartPoll() {
		if (loading) {
			return;
		}
		loading = true;
		try {
			await reload();
			currentQuestionIndex = 0;
			stats = [];
			results = [];
			complete = false;
		} catch (err) {
			Logger.error("Error fetching poll stats", { err });
		} finally {
			loading = false;
		}
	}
</script>

<svelte:head>
	{#each poll.questions as question, i (question.id)}
		{#each question.pollAnswers as option, j (j)}
			{#if option.image?.imageUrl}
				{@const href = transformURL(option.image.imageUrl)}
				{#if i === 0}
					<link rel="preload" {href} as="image" fetchpriority="high" />
				{:else}
					<link rel="prefetch" {href} />
				{/if}
			{/if}
		{/each}
	{/each}
</svelte:head>

{#if pollState !== PollState.PreStart}
	<div class="flex w-full flex-col items-center gap-4.5 rounded-xl border border-(--poll-border-color) bg-(--poll-background-color) p-6" data-flow-button data-linkable-id={id} style={getPollStyleVars($brandKit, $page, config)}>
		<Heading tag="h1" class="w-full font-(--poll-header-font-weight) text-(--poll-header-text-color)" style="text-align: var(--poll-header-text-alignment); font-size: var(--poll-header-text-size);">{poll.heading}</Heading>
		{#if pollState === PollState.Pending}
			<PollCountdown {poll} {mode} onComplete={() => (pollState = PollState.Active)} />
		{:else}
			<div class="grid w-full grid-cols-[auto_1fr_auto] items-center gap-4">
				<div class="font-semibold whitespace-nowrap text-(--poll-secondary-text-color)">
					{#if pollState === PollState.Closed || results.length === poll.questions.length}
						COMPLETE
					{:else}
						{currentQuestionIndex + 1} OF {poll.questions.length}
					{/if}
				</div>
				{#if poll.questions.length > 1}
					<div class="flex min-w-0 justify-center gap-0.75 overflow-hidden">
						{#each poll.questions as _, i (i)}
							{#if i === currentQuestionIndex}
								<div class="h-1.25 w-5 shrink rounded-full bg-(--poll-navigation-color)"></div>
							{:else}
								<div class="h-1.25 w-1.5 shrink rounded-full bg-gray-300"></div>
							{/if}
						{/each}
					</div>
				{/if}
				<div class="flex justify-end whitespace-nowrap">
					<div class="flex items-center gap-2 rounded-full bg-white px-2 py-1">
						{#if pollState === PollState.Closed}
							<Indicator size="xs" color="gray" class="bg-gray-900" />
							<P size="sm" color="text-gray-900" weight="semibold">FINAL</P>
						{:else}
							<Indicator size="xs" color="green" class="animate-pulse" />
							<P size="sm" color="text-green-600" weight="semibold">LIVE</P>
						{/if}
					</div>
				</div>
			</div>
			{#if pollState === PollState.PreResults}
				<P align="center" size="sm" class="text-(--poll-secondary-text-color)">Results will be available soon.</P>
			{:else if pollState === PollState.PostResults}
				<P align="center" size="sm" class="text-(--poll-secondary-text-color)">Poll results are no longer available.</P>
			{:else if pollState === PollState.Closed}
				<PollResults {config} {poll} results={stats} />
			{:else if poll.questions.length > 0}
				{#if !complete}
					{#if poll.window?.end && remainingTime.days === 0 && remainingTime.hours < 1}
						<div class="flex items-center gap-1 rounded-full bg-white px-2 py-1">
							<Icon iconSet="heroicons" name="clock" class="h-4.5 w-4.5 text-yellow-900" />
							<P size="sm" color="text-yellow-900" weight="semibold">{`Closes in ${String(remainingTime.hours).padStart(2, "0")} hr ${String(remainingTime.minutes).padStart(2, "0")} min ${String(remainingTime.seconds).padStart(2, "0")} sec`}</P>
						</div>
					{/if}
					{#if hasResult}
						<P class="w-full font-(--poll-results-helper-font-weight) text-(--poll-results-helper-text-color)" style="text-align: var(--poll-results-helper-text-alignment); font-size: var(--poll-results-helper-text-size);">{poll.resultsHelperText || DEFAULT_RESULTS_HELPER_LABEL}</P>
					{:else}
						<P class="w-full font-(--poll-helper-font-weight) text-(--poll-helper-text-color)" style="text-align: var(--poll-helper-text-alignment); font-size: var(--poll-helper-text-size);">{poll.helperText || DEFAULT_HELPER_LABEL}</P>
					{/if}
					<div class="flex w-full flex-col items-center gap-2">
						{#each currentQuestion.pollAnswers as option, i (i)}
							{@const result = results[currentQuestionIndex]}
							{@const selected = overrideSelection !== undefined ? overrideSelection && i == 0 : result?.userPollAnswer?.value === option.value}
							{@const stats = overrideSelection !== undefined ? create(PollAnswerStatsSchema, { count: BigInt(1), percentage: (1 / currentQuestion.pollAnswers.length) * 100, pollAnswer: option }) : result?.pollAnswerStats[i]}
							{#if i !== 0 && currentQuestion.pollAnswers.length === 2}
								<P size="xs" class="font-semibold text-(--poll-primary-text-color)">{currentQuestion.alternativeText || "VS"}</P>
							{/if}
							<Question {option} {stats} {selected} {readOnly} answerLabel={poll.yourAnswerLabel || DEFAULT_ANSWER_LABEL} onClick={pickWinner} />
						{/each}
						<div class="h-4.5 w-full text-(--poll-secondary-text-color)">
							{#if lastError}
								<div in:fade={{ duration: 200 }}>
									<P align="center" size="xs" color="text-red-500">An error occurred, please try again</P>
								</div>
							{:else if hasResult}
								{@const totalVotes = Number(overrideSelection !== undefined ? currentQuestion.pollAnswers.length : results[currentQuestionIndex].totalPollAnswers)}
								<div in:fade={{ duration: 200 }}>
									<P align="center" size="xs" color="text-(--poll-secondary-text-color)">{formatNumber(totalVotes)} total {pluralize("vote", totalVotes)}</P>
								</div>
							{/if}
						</div>
					</div>
					{#if !hasResult}
						<Button
							data-action-name="skip"
							color="none"
							class={twJoin(`w-full border border-(--poll-skip-button-border-color) bg-(--poll-skip-button-background-color) text-(--poll-skip-button-text-color) hover:bg-(--poll-skip-button-background-hover-color)`, readOnly && "pointer-events-none opacity-100")}
							style="border-radius: var(--poll-skip-button-border-radius); border-color: var(--poll-skip-button-border-color);"
							size={getSkipButtonSize($brandKit, config)}
							disabled={readOnly}
							{loading}
							onclick={skipQuestion}
						>
							{poll.skipButtonText || DEFAULT_SKIP_BUTTON_LABEL}
						</Button>
					{:else if poll && currentQuestionIndex === poll.questions.length - 1}
						<Button
							data-action-name="complete"
							color="none"
							class={twJoin(`w-full border border-(--poll-complete-button-border-color) bg-(--poll-complete-button-background-color) text-(--poll-complete-button-text-color) hover:bg-(--poll-complete-button-background-hover-color)`, readOnly && "pointer-events-none opacity-100")}
							style="border-radius: var(--poll-complete-button-border-radius); border-color: var(--poll-complete-button-border-color);"
							size={getCompleteButtonSize($brandKit, config)}
							disabled={readOnly}
							{loading}
							onclick={nextQuestion}
						>
							{poll.completeButtonText || DEFAULT_COMPLETE_BUTTON_LABEL}
						</Button>
					{:else}
						<Button
							data-action-name="next"
							color="none"
							class={twJoin(`w-full border border-(--poll-submit-button-border-color) bg-(--poll-submit-button-background-color) text-(--poll-submit-button-text-color) hover:bg-(--poll-submit-button-background-hover-color)`, readOnly && "pointer-events-none opacity-100")}
							style="border-radius: var(--poll-submit-button-border-radius); border-color: var(--poll-submit-button-border-color);"
							size={getSubmitButtonSize($brandKit, config)}
							disabled={readOnly}
							{loading}
							onclick={nextQuestion}
						>
							{poll.submitButtonText || DEFAULT_NEXT_BUTTON_LABEL}
						</Button>
					{/if}
				{:else if stats}
					<PollResults {config} {poll} results={stats} />
					<Button
						data-action-name="restart"
						color="none"
						class={twJoin(`w-full border border-(--poll-restart-button-border-color) bg-(--poll-restart-button-background-color) text-(--poll-restart-button-text-color) hover:bg-(--poll-restart-button-background-hover-color)`, readOnly && "pointer-events-none opacity-100")}
						style="border-radius: var(--poll-restart-button-border-radius); border-color: var(--poll-restart-button-border-color);"
						size={getRestartButtonSize($brandKit, config)}
						disabled={readOnly}
						{loading}
						onclick={restartPoll}
					>
						{poll.restartButtonText || DEFAULT_RESTART_BUTTON_LABEL}
					</Button>
				{/if}
			{/if}
		{/if}
	</div>
{/if}
